/**
 * Admin → Users management (Phase 6 of region-scoping).
 *
 * Lists the caller's tenant users and lets an admin create/edit them,
 * including a per-user scope editor (whole-tenant / whole-LD / subregion).
 *
 * Security is enforced server-side; this view mirrors the rules so the UI
 * degrades sensibly:
 *   - Only an ADMIN caller may assign scopes or grant the admin/org_manager
 *     roles. For an ORG_MANAGER caller those controls render disabled with a
 *     hint, and any 403 from the API is surfaced inline.
 *
 * Invites: an account created by the launch wizard (or by `POST /admin/users`
 * with no password) exists but is unreachable until its holder redeems an
 * invite and picks their own credential — `must_set_password` is true for
 * exactly that window, and the row badges it. The per-row invite button
 * re-mints the link, which is the supported way back in for someone who lost
 * theirs. The raw token is shown ONCE and is not recoverable; the banner says
 * so, because an admin who closes it will have to mint another.
 *
 * "Chase invites" is Easy button #3 (memory-bank/plans/easy-buttons.md §3):
 * the same per-row re-issue, N times, for everyone who was invited and never
 * signed in. It appears only when something is actually pending, and the count
 * on it is a hint — the server decides who is chaseable, since this view knows
 * nothing about whose link has expired or whose turf sits outside the caller's.
 *
 * Styling matches the other admin views (semantic Tailwind tokens:
 * bg-bg-base/bg-bg-raised, text-fg-*, bg-accent, status-*).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  Copy,
  Mail,
  MailWarning,
  Plus,
  X,
  Users as UsersIcon,
  Trash2,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { InfoTip } from '@/components/InfoTip';
import { useMapRegions } from '@/context/MapRegionsContext';
import { regionKeyForScope, type MapRegionCatalog } from '@/api/geoRegions';
import { inviteUrl } from '@/lib/inviteLinks';
import ChaseInvitesModal from '@/components/admin/ChaseInvitesModal';
import { fetchRoleOptions, type RoleOption as ApiRoleOption } from '@/api/launch';
import { listCandidates } from '@/api/candidates';
import type { CandidateListItem } from '@/types/candidate';
import type { AdminUser, UserScopeInput } from '@/types';
import {
  listAdminUsers,
  createAdminUser,
  updateAdminUser,
  deactivateAdminUser,
  reactivateAdminUser,
  reissueInvite,
  type CreateUserPayload,
  type UpdateUserPayload,
} from '@/api/adminUsers';

// ---------------------------------------------------------------------------
// Role options.
//
// These come from `/admin/users/role-options`, which serves
// `provisioning.grantable_roles(caller)` — the same matrix the server enforces
// on write. The list used to be hardcoded here, and had drifted: it was
// missing `candidate` and `viewer` entirely, and its `adminOnly` flags were a
// second, hand-maintained copy of the grant matrix. That is the shape of the
// SMS_MANAGER lockout (see `lib/roles.ts`) — a role rename or a matrix change
// reaches the server and not the picker, and the mismatch only shows up as a
// 403 after somebody has filled in a form.
//
// FALLBACK is used only when the endpoint is unreachable, so the drawer still
// renders something. It is deliberately the conservative floor rather than a
// second copy of the matrix: every real ceiling still comes from the server.
// ---------------------------------------------------------------------------
const FALLBACK_ROLES: ApiRoleOption[] = [
  { value: 'coordinator', rank: 50, grantable: true },
  { value: 'canvasser', rank: 20, grantable: true },
  { value: 'viewer', rank: 10, grantable: true },
];

/** `sms_manager` -> `SMS Manager`, `org_manager` -> `Org Manager`. */
function roleLabel(value: string): string {
  return value
    .split('_')
    .map((w) => (w === 'sms' ? 'SMS' : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}


// ---------------------------------------------------------------------------
// Local, editable scope-row model. `kind: 'none'` is an unfinished/whole-tenant
// row that is dropped before submit.
// ---------------------------------------------------------------------------
type ScopeKind = 'none' | 'ld' | 'subregion';
interface ScopeRow {
  kind: ScopeKind;
  /** For kind==='ld' this is the LD string; for 'subregion' it's the turf key. */
  value: string;
}

/**
 * Map a resolved server scope back into an editable row.
 *
 * The turf catalog is threaded in rather than imported: it is tenant data
 * (`GET /geo/regions`) held in React state, so a module-level copy would be
 * either stale or empty. `precincts` is the actual precinct list where the
 * caller has one — matching on the set is exact, where the old
 * match-by-COUNT would happily hand back a different turf that happened to
 * hold the same number of precincts.
 */
function scopeToRow(
  regions: MapRegionCatalog,
  kind: 'ld' | 'subregion',
  ld: string,
  precincts: string[] | null,
): ScopeRow {
  if (kind === 'ld') return { kind: 'ld', value: ld };
  const key = regionKeyForScope(regions, ld, precincts);
  return { kind: 'subregion', value: key ?? regions.keys[0] ?? '' };
}

/** Build the API write payload from the editable rows, dropping 'none' rows. */
function rowsToScopeInputs(
  regions: MapRegionCatalog,
  rows: ScopeRow[],
): UserScopeInput[] {
  const out: UserScopeInput[] = [];
  for (const row of rows) {
    if (row.kind === 'ld' && row.value) {
      out.push({ kind: 'ld', ld: String(row.value) });
    } else if (row.kind === 'subregion' && row.value) {
      const region = regions.byKey[row.value];
      if (region) {
        out.push({
          kind: 'subregion',
          ld: String(region.ld),
          // Precincts go out as STRINGS — the catalog keeps the wire copy
          // precisely so no write path has to re-coerce them.
          precincts: region.precinctsAsStrings,
        });
      }
    }
  }
  return out;
}

function extractError(err: unknown, fallback: string): string {
  const resp = (err as any)?.response;
  if (resp?.status === 403) {
    return (
      resp?.data?.detail ||
      'You do not have permission for this action. Assigning scopes and the admin / org_manager roles requires an administrator.'
    );
  }
  return resp?.data?.detail || (err as any)?.message || fallback;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function scopeSummary(u: AdminUser): string {
  if (!u.scopes || u.scopes.length === 0) return 'Whole tenant';
  return u.scopes.map((s) => s.label).join('; ');
}

// ---------------------------------------------------------------------------
// Role badge.
// ---------------------------------------------------------------------------
function RoleBadge({ role }: { role: string }) {
  return (
    <span className="inline-block rounded-full bg-bg-base px-2 py-0.5 text-xs font-semibold text-fg-secondary border border-border-subtle">
      {role}
    </span>
  );
}

// ===========================================================================
// Drawer — create/edit form.
// ===========================================================================
interface DrawerState {
  mode: 'create' | 'edit';
  user: AdminUser | null;
}

interface UserDrawerProps {
  state: DrawerState;
  isAdmin: boolean;
  roleOptions: ApiRoleOption[];
  candidates: CandidateListItem[];
  onClose: () => void;
  /** Receives the created/updated row so the caller can surface an invite. */
  onSaved: (saved?: AdminUser) => void;
}

function UserDrawer({
  state,
  isAdmin,
  roleOptions,
  candidates,
  onClose,
  onSaved,
}: UserDrawerProps) {
  const editing = state.mode === 'edit';
  const u = state.user;
  // The parent only mounts this drawer once the catalog has resolved, so the
  // scope-row initializer below can trust it.
  const { catalog: regions } = useMapRegions();

  const [email, setEmail] = useState(u?.email ?? '');
  const [password, setPassword] = useState('');
  // Creating: invite by default. The account is unusable until the invitee
  // redeems it and picks their own password, which is the only way nobody ends
  // up knowing a credential they did not choose.
  const [useInvite, setUseInvite] = useState(true);
  const [fullName, setFullName] = useState(u?.full_name ?? '');
  const [role, setRole] = useState(u?.role ?? 'canvasser');
  const [candidateId, setCandidateId] = useState<number | ''>('');
  const [countyWideConfirmed, setCountyWideConfirmed] = useState(false);
  const [isActive, setIsActive] = useState(u?.is_active ?? true);
  const [scopeRows, setScopeRows] = useState<ScopeRow[]>(
    (u?.scopes ?? []).map((s) => scopeToRow(regions, s.kind, s.ld, s.precincts)),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addScopeRow = () =>
    setScopeRows((r) => [...r, { kind: 'subregion', value: regions.keys[0] ?? '' }]);
  const removeScopeRow = (i: number) => setScopeRows((r) => r.filter((_, idx) => idx !== i));
  const updateScopeRow = (i: number, next: Partial<ScopeRow>) =>
    setScopeRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...next } : row)));

  const isCandidateRole = role === 'candidate';
  const selectedCandidate = candidates.find((c) => c.id === candidateId) ?? null;
  // A candidate with no reach produces a TENANT-GLOBAL login. The server
  // refuses that with a 409 until it is confirmed, so ask here rather than
  // letting the submit bounce.
  const needsCountyWide =
    isCandidateRole && selectedCandidate !== null && selectedCandidate.scopes.length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!editing && !useInvite && !password) {
      setError('Set a password, or switch to sending an invite link.');
      return;
    }
    if (!editing && isCandidateRole && candidateId === '') {
      setError('Choose which candidate this login is for.');
      return;
    }
    if (needsCountyWide && !countyWideConfirmed) {
      setError(
        'That candidate has no turf, so this login would see the whole county. ' +
          'Confirm the office is county-wide, or give the candidate a district first.',
      );
      return;
    }

    const scopes = rowsToScopeInputs(regions, scopeRows);
    setSaving(true);
    try {
      if (editing && u) {
        const payload: UpdateUserPayload = {
          full_name: fullName || undefined,
          role,
          is_active: isActive,
        };
        if (password) payload.password = password;
        // Only send scopes when the caller is allowed to set them; otherwise
        // omit so we don't clobber existing scopes server-side.
        if (isAdmin) payload.scopes = scopes;
        onSaved(await updateAdminUser(u.id, payload));
      } else {
        const payload: CreateUserPayload = {
          email,
          full_name: fullName || undefined,
          role,
        };
        // Omitting `password` IS the invite flow — the server mints a token
        // and returns it once. Sending an empty string would be a password.
        if (!useInvite) payload.password = password;
        if (isCandidateRole && candidateId !== '') {
          payload.candidate_id = candidateId;
          // Reach is mirrored from the candidate; sending scopes alongside is
          // a 400. The scope editor is hidden for this role for that reason.
          if (countyWideConfirmed) payload.county_wide_confirmed = true;
        } else if (isAdmin && scopes.length > 0) {
          payload.scopes = scopes;
        }
        onSaved(await createAdminUser(payload));
      }
    } catch (err) {
      setError(extractError(err, 'Could not save the user. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'w-full rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-sm text-fg-primary placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-accent';

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 flex h-full w-full max-w-md flex-col overflow-y-auto bg-bg-raised shadow-xl">
        <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
          <h2 className="text-base font-semibold text-fg-primary">
            {editing ? 'Edit user' : 'New user'}
          </h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-fg-muted hover:bg-bg-base hover:text-fg-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-4 px-5 py-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-fg-secondary">Email</label>
            <input
              type="email"
              required
              value={email}
              disabled={editing}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls + (editing ? ' opacity-60' : '')}
              placeholder="user@example.org"
            />
          </div>

          {!editing && (
            <div className="rounded-md border border-border-subtle bg-bg-base p-3">
              <div className="flex items-start gap-1">
                <label className="flex items-start gap-2 text-sm text-fg-secondary">
                  <input
                    type="checkbox"
                    checked={useInvite}
                    onChange={(e) => setUseInvite(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-accent"
                  />
                  <span>
                    <span className="font-semibold text-fg-primary">
                      Send an invite link
                    </span>
                    <span className="mt-0.5 block text-xs text-fg-muted">
                      The account is created with no working password and you
                      get a one-time link to hand over. They choose the
                      credential — nobody else ever knows it.
                    </span>
                  </span>
                </label>
                <InfoTip termId="invite_link" compact />
              </div>
            </div>
          )}

          {(editing || !useInvite) && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-fg-secondary">
                {editing ? 'New password (leave blank to keep)' : 'Password'}
              </label>
              <input
                type="password"
                required={!editing && !useInvite}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder={editing ? '••••••••' : 'Set a password'}
                autoComplete="new-password"
              />
              {editing && (
                <p className="mt-1 text-xs text-fg-muted">
                  Prefer the row&rsquo;s invite button for someone who lost
                  access — it keeps the password in one person&rsquo;s hands.
                </p>
              )}
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-semibold text-fg-secondary">Full name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={inputCls}
              placeholder="Jane Doe"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-fg-secondary">
              Role
              <InfoTip termId="user_role" compact className="ml-1 align-middle" />
            </label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls}>
              {roleOptions.map((opt) => (
                <option key={opt.value} value={opt.value} disabled={!opt.grantable}>
                  {roleLabel(opt.value)}
                  {opt.grantable ? '' : ' (not available to you)'}
                </option>
              ))}
            </select>
            {!isAdmin && (
              <p className="mt-1 text-xs text-fg-muted">
                Greyed-out roles sit above what your own role may grant.
              </p>
            )}
          </div>

          {/* Candidate link ------------------------------------------------ */}
          {!editing && isCandidateRole && (
            <div className="rounded-md border border-border-subtle bg-bg-base p-3">
              <label className="mb-1 block text-xs font-semibold text-fg-secondary">
                Which candidate is this the login for?
                <InfoTip termId="candidate_login" compact className="ml-1 align-middle" />
              </label>
              <select
                value={candidateId}
                onChange={(e) => {
                  setCandidateId(e.target.value === '' ? '' : Number(e.target.value));
                  setCountyWideConfirmed(false);
                }}
                className={inputCls}
              >
                <option value="">Choose a candidate…</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.full_name} — {c.office}
                    {c.scopes.length === 0 ? ' (no turf)' : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-fg-muted">
                Their turf is copied onto the login, so they see their own
                district and nothing else. Without this link the account would
                have a candidate&rsquo;s role and the whole county&rsquo;s data.
              </p>

              {needsCountyWide && (
                <div className="mt-2 flex items-start gap-1">
                <label className="flex items-start gap-2 text-xs text-status-warning">
                  <input
                    type="checkbox"
                    checked={countyWideConfirmed}
                    onChange={(e) => setCountyWideConfirmed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-accent"
                  />
                  <span>
                    {selectedCandidate?.full_name} has no turf, so this login
                    will see the <strong>entire county</strong>. Confirm the
                    office really is county-wide (Sheriff, Assessor).
                  </span>
                </label>
                <InfoTip termId="county_wide_office" compact />
                </div>
              )}

              {candidates.length === 0 && (
                <p className="mt-1 text-xs text-status-warning">
                  No active candidates yet. Add one under Candidates first.
                </p>
              )}
            </div>
          )}

          {editing && (
            <label className="flex items-center gap-2 text-sm text-fg-secondary">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 accent-accent"
              />
              Active
            </label>
          )}

          {/* Scope editor -------------------------------------------------- */}
          {!editing && isCandidateRole ? (
            <p className="rounded-md border border-border-subtle bg-bg-base p-3 text-xs text-fg-muted">
              <span className="font-semibold text-fg-secondary">Region scopes</span>
              <InfoTip termId="region_scope" compact className="ml-1 align-middle" />
              <br />
              Set from the candidate&rsquo;s own reach, not here. Sending scopes
              alongside a candidate link is refused server-side, because the two
              would disagree and the mirror wins.
            </p>
          ) : (
          <div className="rounded-md border border-border-subtle bg-bg-base p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-fg-secondary">
                Region scopes
                <InfoTip termId="region_scope" compact className="ml-1 align-middle" />
              </span>
              <button
                type="button"
                onClick={addScopeRow}
                // No turfs means there is nothing a scope row could name. An
                // enabled button would add a row whose select is empty and
                // whose submit silently drops it — leaving the account
                // TENANT-GLOBAL, the widest setting, without saying so.
                disabled={!isAdmin || regions.isEmpty}
                className="inline-flex items-center gap-1 rounded bg-accent px-2 py-1 text-xs font-semibold text-white hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Plus size={12} /> Add turf
              </button>
            </div>

            {!isAdmin ? (
              <p className="text-xs text-fg-muted">
                Scope assignment is available to administrators only. This user keeps its current
                access.
              </p>
            ) : regions.isEmpty ? (
              <p className="text-xs text-fg-muted">
                No turfs are loaded for this tenant yet, so there is nothing to
                scope to. Until they are, every account here can see the{' '}
                <strong>whole tenant</strong>. Contact platform support to load
                them.
              </p>
            ) : scopeRows.length === 0 ? (
              <p className="text-xs text-fg-muted">
                No scopes — this user can see the <strong>whole tenant</strong>. Add a turf to
                restrict them.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {scopeRows.map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <select
                      value={row.kind}
                      onChange={(e) => {
                        const kind = e.target.value as ScopeKind;
                        const value =
                          kind === 'ld'
                            ? regions.ldOptions[0] ?? ''
                            : kind === 'subregion'
                            ? regions.keys[0] ?? ''
                            : '';
                        updateScopeRow(i, { kind, value });
                      }}
                      className="rounded border border-border-subtle bg-bg-raised px-2 py-1 text-xs text-fg-primary"
                    >
                      <option value="subregion">Subregion</option>
                      <option value="ld">Whole LD</option>
                    </select>

                    {row.kind === 'ld' ? (
                      <select
                        value={row.value}
                        onChange={(e) => updateScopeRow(i, { value: e.target.value })}
                        className="flex-1 rounded border border-border-subtle bg-bg-raised px-2 py-1 text-xs text-fg-primary"
                      >
                        {regions.ldOptions.map((ld) => (
                          <option key={ld} value={ld}>
                            LD {ld}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={row.value}
                        onChange={(e) => updateScopeRow(i, { value: e.target.value })}
                        className="flex-1 rounded border border-border-subtle bg-bg-raised px-2 py-1 text-xs text-fg-primary"
                      >
                        {regions.keys.map((k) => (
                          <option key={k} value={k}>
                            {k} — {regions.byKey[k].name} (LD {regions.byKey[k].ld})
                          </option>
                        ))}
                      </select>
                    )}

                    <button
                      type="button"
                      onClick={() => removeScopeRow(i)}
                      className="rounded p-1 text-fg-muted hover:bg-bg-raised hover:text-status-danger"
                      aria-label="Remove turf"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {error && (
            <div className="rounded-md border border-status-danger-bg/30 bg-status-danger-bg/20 px-3 py-2 text-sm text-status-danger">
              {error}
            </div>
          )}

          <div className="mt-auto flex items-center justify-end gap-2 border-t border-border-subtle pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border-subtle px-3 py-2 text-sm font-semibold text-fg-secondary hover:bg-bg-base"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50"
            >
              {saving
                ? 'Saving…'
                : editing
                ? 'Save changes'
                : useInvite
                ? 'Create + get invite link'
                : 'Create user'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===========================================================================
// Main view.
// ===========================================================================
export function UsersAdminView() {
  const { user } = useAuth();
  const isAdmin = (user?.role ?? '').toString().toUpperCase() === 'ADMIN';
  // The drawer's scope rows are seeded from the catalog in a useState
  // initializer, which runs once. Holding the drawer until the catalog has
  // resolved is what lets that initializer be a one-shot instead of needing a
  // re-derive effect that could clobber a half-finished edit.
  const { loading: regionsLoading } = useMapRegions();

  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [chasing, setChasing] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  // The freshly-minted link, held only in memory. Closing this loses it.
  const [invite, setInvite] = useState<{ email: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);
  // Server-served grant matrix + the slate, both needed by the drawer. Loaded
  // once here rather than per-open so the picker is populated before the form
  // is, and a failure degrades to FALLBACK_ROLES instead of an empty select.
  const [roleOptions, setRoleOptions] = useState<ApiRoleOption[]>(FALLBACK_ROLES);
  const [candidates, setCandidates] = useState<CandidateListItem[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAdminUsers();
      setUsers(data);
    } catch (err) {
      setError(extractError(err, 'Could not load users.'));
      setUsers(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    // Neither is fatal: without roles the drawer falls back, and without the
    // slate only the candidate picker is empty. Failing the whole view because
    // a secondary list 403'd would be worse than either.
    fetchRoleOptions()
      .then((r) => !cancelled && r.length > 0 && setRoleOptions(r))
      .catch(() => undefined);
    listCandidates(true)
      .then((c) => !cancelled && setCandidates(c))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleActive = async (u: AdminUser) => {
    setRowError(null);
    setBusyId(u.id);
    try {
      if (u.is_active) {
        await deactivateAdminUser(u.id);
      } else {
        await reactivateAdminUser(u.id);
      }
      await load();
    } catch (err) {
      setRowError(extractError(err, 'Could not update the user.'));
    } finally {
      setBusyId(null);
    }
  };

  const sendInvite = async (u: AdminUser) => {
    setRowError(null);
    setBusyId(u.id);
    try {
      const updated = await reissueInvite(u.id);
      if (!updated.invite_token) {
        // Should not happen — the endpoint always mints. Say so rather than
        // rendering `?invite=undefined`, which would look like a working link.
        setRowError('The server did not return an invite token. Nothing was sent.');
        return;
      }
      setCopied(false);
      setInvite({
        email: u.email,
        url: inviteUrl(window.location.origin, updated.invite_token),
      });
      await load();
    } catch (err) {
      setRowError(extractError(err, 'Could not issue an invite.'));
    } finally {
      setBusyId(null);
    }
  };

  const onSaved = useCallback(
    (saved?: AdminUser) => {
      setDrawer(null);
      // A create with no password comes back with the raw token exactly once.
      // Dropping it here would strand the new account with no way in.
      if (saved?.invite_token) {
        setCopied(false);
        setInvite({
          email: saved.email,
          url: inviteUrl(window.location.origin, saved.invite_token),
        });
      }
      void load();
    },
    [load],
  );

  // How many accounts are badged "Invite pending" right now. Drives whether
  // the chase button is offered at all: with nothing unclaimed it would be a
  // control that can only tell you there is nothing to do.
  //
  // This is a HINT, not the gate. The server decides who is actually chaseable
  // — this count does not know about expired-vs-live links or about the
  // caller's role ceiling and turf, and must not pretend to.
  const pendingCount = useMemo(
    () => (users ?? []).filter((u) => u.must_set_password && u.is_active).length,
    [users],
  );

  const content = useMemo(() => {
    if (loading) {
      return <div className="py-16 text-center text-fg-muted">Loading users…</div>;
    }
    if (error) {
      return (
        <div className="mx-auto max-w-2xl rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="mb-1 text-lg font-semibold text-status-danger">Couldn’t load users</h2>
          <p className="text-fg-secondary">{error}</p>
          <button
            onClick={() => void load()}
            className="mt-3 rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-white hover:bg-accent-hover"
          >
            Retry
          </button>
        </div>
      );
    }
    if (!users || users.length === 0) {
      return (
        <div className="py-16 text-center text-fg-muted">
          No users yet. Use “New user” to add one.
        </div>
      );
    }
    return (
      <div className="overflow-x-auto rounded-lg border border-border-subtle">
        <table className="w-full text-sm">
          <thead className="bg-bg-base text-left text-xs uppercase text-fg-muted">
            <tr>
              <th className="px-4 py-2 font-semibold">Email</th>
              <th className="px-4 py-2 font-semibold">Name</th>
              <th className="px-4 py-2 font-semibold">
                Role
                <InfoTip termId="user_role" compact className="ml-1 align-middle" />
              </th>
              <th className="px-4 py-2 font-semibold">
                Scope
                <InfoTip termId="region_scope" compact className="ml-1 align-middle" />
              </th>
              <th className="px-4 py-2 font-semibold">Last login</th>
              <th className="px-4 py-2 font-semibold">Active</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {users.map((u) => (
              <tr key={u.id} className={u.is_active ? '' : 'opacity-60'}>
                <td className="px-4 py-2 text-fg-primary">
                  {u.email}
                  {u.must_set_password && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-status-warning-bg/30 px-2 py-0.5 text-xs font-semibold text-status-warning">
                      Invite pending
                      <InfoTip termId="invite_pending" compact className="ml-0.5" />
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-fg-secondary">{u.full_name || '—'}</td>
                <td className="px-4 py-2">
                  <RoleBadge role={u.role} />
                </td>
                <td className="px-4 py-2 text-fg-secondary">{scopeSummary(u)}</td>
                <td className="px-4 py-2 text-fg-muted">{fmtDate(u.last_login_at)}</td>
                <td className="px-4 py-2">
                  <button
                    onClick={() => void toggleActive(u)}
                    disabled={busyId === u.id}
                    className={
                      'rounded-full px-2 py-0.5 text-xs font-semibold disabled:opacity-50 ' +
                      (u.is_active
                        ? 'bg-status-success-bg/30 text-status-success'
                        : 'bg-bg-base text-fg-muted border border-border-subtle')
                    }
                    title={u.is_active ? 'Click to deactivate' : 'Click to reactivate'}
                  >
                    {u.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => void sendInvite(u)}
                      disabled={busyId === u.id || !u.is_active}
                      title={
                        !u.is_active
                          ? 'Reactivate the account before inviting them back.'
                          : u.must_set_password
                          ? 'Issue an invite link so they can set their password.'
                          : 'Issue a fresh link. Their current password stops working only once they redeem it; any outstanding invite dies now.'
                      }
                      className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle px-2.5 py-1 text-xs font-semibold text-fg-secondary hover:bg-bg-base disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Mail size={12} />
                      {u.must_set_password ? 'Invite link' : 'Re-invite'}
                    </button>
                    <button
                      onClick={() => setDrawer({ mode: 'edit', user: u })}
                      className="rounded-md border border-border-subtle px-2.5 py-1 text-xs font-semibold text-fg-secondary hover:bg-bg-base"
                    >
                      Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, [loading, error, users, busyId, load]);

  return (
    <div className="min-h-dvh bg-bg-base p-6 text-fg-primary">
      <div className="mx-auto max-w-5xl">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UsersIcon size={20} className="text-accent" />
            <div>
              <h1 className="text-lg font-semibold text-fg-primary">Users</h1>
              <p className="text-sm text-fg-muted">
                Manage tenant members, roles, and region scopes. New members get
                an invite link
                <InfoTip termId="invite_link" compact className="mx-0.5 align-middle" />
                and choose their own password.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pendingCount > 0 && (
              <button
                onClick={() => setChasing(true)}
                title="Issue fresh links for everyone who was invited and never signed in."
                className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle px-3 py-2 text-sm font-semibold text-fg-secondary hover:bg-bg-raised"
              >
                <MailWarning size={16} />
                Chase invites
                <span className="rounded-full bg-status-warning-bg/30 px-1.5 text-xs font-bold text-status-warning">
                  {pendingCount}
                </span>
              </button>
            )}
            {pendingCount > 0 && <InfoTip termId="chase_invites" compact />}
            <button
              onClick={() => setDrawer({ mode: 'create', user: null })}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-2 text-sm font-semibold text-white hover:bg-accent-hover"
            >
              <Plus size={16} /> New user
            </button>
          </div>
        </div>

        {!isAdmin && (
          <div className="mb-4 rounded-md border border-border-subtle bg-bg-raised px-4 py-2 text-sm text-fg-secondary">
            You can manage users, but assigning region scopes and the Admin / Org Manager roles
            requires an administrator.
          </div>
        )}

        {rowError && (
          <div className="mb-4 rounded-md border border-status-danger-bg/30 bg-status-danger-bg/20 px-4 py-2 text-sm text-status-danger">
            {rowError}
          </div>
        )}

        {invite && (
          <div className="mb-4 rounded-md border border-border-subtle bg-bg-raised px-4 py-3">
            <div className="mb-1 flex items-start justify-between gap-3">
              <div className="text-sm font-semibold text-fg-primary">
                Invite link for {invite.email}
                <InfoTip termId="invite_link" compact className="ml-1 align-middle" />
              </div>
              <button
                onClick={() => setInvite(null)}
                className="rounded p-1 text-fg-muted hover:bg-bg-base hover:text-fg-primary"
                aria-label="Dismiss invite link"
              >
                <X size={16} />
              </button>
            </div>
            <p className="mb-2 text-xs text-fg-muted">
              Send it to them yourself — there is no mailer.{' '}
              <strong className="text-fg-secondary">
                This is the only time it is shown
              </strong>
              , it works once, and it expires in 14 days. Any earlier link for
              this person is now dead.
            </p>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded border border-border-subtle bg-bg-base px-2 py-1.5 text-xs text-fg-secondary">
                {invite.url}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(invite.url);
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1500);
                }}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border-subtle px-2.5 py-1.5 text-xs font-semibold text-fg-secondary hover:bg-bg-base"
              >
                {copied ? (
                  <>
                    <Check size={12} className="text-status-success" /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={12} /> Copy link
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {content}
      </div>

      {chasing && (
        <ChaseInvitesModal
          onClose={() => setChasing(false)}
          // Refresh behind the modal rather than on close: the links are still
          // on screen and unrecoverable, so the table has to catch up without
          // the caller having to dismiss them to see it.
          onReissued={() => void load()}
        />
      )}

      {drawer && !regionsLoading && (
        <UserDrawer
          state={drawer}
          isAdmin={isAdmin}
          roleOptions={roleOptions}
          candidates={candidates}
          onClose={() => setDrawer(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

export default UsersAdminView;
